import {AxiosRequestConfig} from 'axios';
import {WorkExperienceService} from './work-experience.service';
import Logger from '../../logger';
import {WorkExperience} from '../../models/work-experience.model';
import {getAuthToken} from '../../utils';
import {APIServiceImpl, ServiceResponse} from '../api';
import {WorkExperienceCreateParams, WorkExperienceUpdateParams} from './work-experience-params';

export class WorkExperienceServiceImpl extends APIServiceImpl implements WorkExperienceService {
  static readonly CREATE_WORK_EXPERIENCE = '/accounts/:accountId/organizations/:organizationId/work-experiences';
  static readonly GET_WORK_EXPERIENCES = '/accounts/:accountId/organizations/:organizationId/work-experiences';
  static readonly GET_WORK_EXPERIENCE = '/work-experiences/:workExperienceId';
  static readonly UPDATE_WORK_EXPERIENCE = '/accounts/:accountId/organizations/:organizationId/work-experiences/:workExperienceId';
  static readonly DELETE_WORK_EXPERIENCE = '/accounts/:accountId/organizations/:organizationId/work-experiences/:workExperienceId';

  constructor() {
    super();
    this.service.interceptors.request.use((request): AxiosRequestConfig => {
      const token = getAuthToken();
      token && (request.headers.Authorization = `Bearer ${token}`);
      return request;
    });
  }

  async createWorkExperience(params: WorkExperienceCreateParams): Promise<ServiceResponse<WorkExperience>> {
    const url = WorkExperienceServiceImpl.CREATE_WORK_EXPERIENCE.replace(':accountId', params.accountId).replace(
      ':organizationId',
      params.organizationId,
    );

    try {
      const {data: res} = await this.post(url, {
        employmentTenure: params.employmentTenure,
        isCurrentlyWorking: params.isCurrentlyWorking,
        jobTitleId: params.jobTitleId,
        jobDescription: params.jobDescription,
        type: params.type,
        location: params.location,
      });
      return new ServiceResponse<WorkExperience>(new WorkExperience(res));
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`POST ${url} ${JSON.stringify(params)}: ${e.toString()}: ${error}`);
      return new ServiceResponse<WorkExperience>(undefined, error);
    }
  }

  async getAllByAccountOrganization(accountId: string, organizationId: string): Promise<ServiceResponse<WorkExperience[]>> {
    const url = WorkExperienceServiceImpl.GET_WORK_EXPERIENCES.replace(':accountId', accountId).replace(':organizationId', organizationId);

    try {
      const {data} = await this.get(url);
      return new ServiceResponse<WorkExperience[]>(data.map((item: any) => new WorkExperience(item)));
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`GET ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<WorkExperience[]>(undefined, error);
    }
  }

  async getWorkExperience(workExperienceId: string): Promise<ServiceResponse<WorkExperience>> {
    const url = WorkExperienceServiceImpl.GET_WORK_EXPERIENCE.replace(':workExperienceId', workExperienceId);

    try {
      const {data} = await this.get(url);
      return new ServiceResponse<WorkExperience>(new WorkExperience(data));
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`GET ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<WorkExperience>(undefined, error);
    }
  }

  async updateWorkExperience(
    workExperienceId: string,
    params: WorkExperienceUpdateParams & {accountId: string},
  ): Promise<ServiceResponse<WorkExperience>> {
    if (!params.organizationId) {
      const error = 'OrganizationId is required for updating work experience';
      Logger.error(`PUT work experience ${workExperienceId}: ${error}`);
      return new ServiceResponse<WorkExperience>(undefined, error);
    }

    if (!params.accountId) {
      const error = 'AccountId is required for updating work experience';
      Logger.error(`PUT work experience ${workExperienceId}: ${error}`);
      return new ServiceResponse<WorkExperience>(undefined, error);
    }

    const url = WorkExperienceServiceImpl.UPDATE_WORK_EXPERIENCE.replace(':accountId', params.accountId)
      .replace(':organizationId', params.organizationId)
      .replace(':workExperienceId', workExperienceId);

    try {
      const {data} = await this.put(url, {
        organizationId: params.organizationId,
        employmentTenure: params.employmentTenure,
        isCurrentlyWorking: params.isCurrentlyWorking,
        jobTitleId: params.jobTitleId,
        jobDescription: params.jobDescription,
        type: params.type,
        location: params.location,
      });
      return new ServiceResponse<WorkExperience>(new WorkExperience(data));
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`PUT ${url} ${JSON.stringify(params)}: ${e.toString()}: ${error}`);
      return new ServiceResponse<WorkExperience>(undefined, error);
    }
  }

  async deleteWorkExperience(workExperienceId: string, accountId: string, organizationId: string): Promise<ServiceResponse<void>> {
    const url = WorkExperienceServiceImpl.DELETE_WORK_EXPERIENCE.replace(':accountId', accountId)
      .replace(':organizationId', organizationId)
      .replace(':workExperienceId', workExperienceId);

    try {
      await this.delete(url);
      return new ServiceResponse<void>();
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`DELETE ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<void>(undefined, error);
    }
  }
}
